import * as React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  BlockOutlined,
  ChatBubbleOutlineOutlined,
  CheckCircleOutlineOutlined,
  Close,
  Credits,
  DescriptionOutlined,
  ErrorOutlineOutlined,
  FeedbackOutlined,
  NewReleasesOutlined,
  NotificationsOutlined,
  PersonAddOutlined,
} from "@/components/icons";
import { useMe, useSetActiveOrg } from "@/api/hooks";
import {
  type NotificationItem,
  useDismissAllNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationList,
  useSetNotificationHidden,
} from "@/api/notifications";
import { apiErrorText } from "@/features/sessions/errorMessages";
import { useConfirm } from "@/hooks/useConfirm";
import { useToast } from "@/hooks/useToast";
import { relativeAge } from "@/i18n/format";
import { useNow } from "@/hooks/useNow";

import { notificationTitle } from "./notificationText";

const ICONS: Record<string, React.ElementType> = {
  session_added: PersonAddOutlined,
  decision_waiting: ChatBubbleOutlineOutlined,
  turn_finished: CheckCircleOutlineOutlined,
  turn_failed: ErrorOutlineOutlined,
  document_ready: DescriptionOutlined,
  credits_exhausted: Credits,
  seat_over_capacity: BlockOutlined,
  request_updated: FeedbackOutlined,
  roadmap_shipped: NewReleasesOutlined,
};

const TITLE_ID = "notification-drawer-title";

/** Read by screen readers, not shown: the dot says "unread" to the eye. */
const SCREEN_READER_ONLY = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

/** The list behind the bell: newest first, a page at a time ("Load
 *  older"), full width on a phone.
 *
 *  It opens above the app bar (which sits at ``drawer + 1``): at the
 *  default drawer level its header row, with the close button and
 *  "Mark all read", was under the bar, and a phone user with nothing
 *  to open had no way out.
 *
 *  A click marks the row read and opens its link. When another org is
 *  pinned, a row from a different org is labelled with it, and opening it
 *  pins that org first, so the page it lands on shows the right tenant;
 *  an admin page is always opened under its own org.
 *
 *  Each row's × hides it for this user ("Clear all" hides every shown
 *  one, after a confirm); nothing is deleted. "Show hidden" lists them
 *  again, muted, each with a Restore button. Hidden and read are
 *  separate: hiding does not mark a row read. */
export function NotificationDrawer(props: {
  open: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const { open, onClose } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const me = useMe();
  const setActiveOrg = useSetActiveOrg();
  const confirm = useConfirm();
  const [showHidden, setShowHidden] = React.useState(false);
  const list = useNotificationList({ enabled: open, includeHidden: showHidden });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const setHidden = useSetNotificationHidden();
  const dismissAll = useDismissAllNotifications();

  // Only a PERSISTED pin scopes the app; unpinned, the backend still fills
  // ``active_org_slug`` with a display default that means nothing here.
  const scope = me.data?.admin_scope;
  const pinnedSlug = scope?.active_org_pinned ? (scope.active_org_slug ?? null) : null;
  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  const unread = list.data?.pages[0]?.unread_count ?? 0;
  // The newest row on show: "Clear all" hides up to it and no further.
  const newestShown = items.find((item) => !item.dismissed_at)?.created_at ?? null;
  const otherOrg = (item: NotificationItem): string | null =>
    item.org && pinnedSlug && item.org.slug !== pinnedSlug ? item.org.name : null;

  const openItem = async (item: NotificationItem) => {
    if (!item.read_at) markRead.mutate(item.id);
    onClose();
    if (!item.link) return;
    // Pin the row's org when a DIFFERENT org is pinned (the page would show
    // the wrong tenant), or for an admin page, which is org-scoped by the
    // pin. An unpinned user opening a session stays unpinned.
    const needsPin =
      item.org &&
      item.org.slug !== pinnedSlug &&
      (pinnedSlug !== null || item.link.startsWith("/app/admin/"));
    if (item.org && needsPin) {
      try {
        await setActiveOrg.mutateAsync({ slug: item.org.slug });
        void qc.invalidateQueries();
      } catch (err) {
        toast(apiErrorText(t, err), "error");
        return;
      }
    }
    navigate(item.link);
  };

  // Where focus goes once a row's × is pressed: its button leaves the DOM
  // with the row, and focus would fall back to the drawer itself.
  const listAreaRef = React.useRef<HTMLElement | null>(null);
  // Set by ``hide``, consumed by the effect below once the refetch has
  // rendered the target. Refs: moving focus needs no render of its own.
  const focusListWhenEmpty = React.useRef(false);
  const pendingRestoreFocus = React.useRef<string | null>(null);

  const focusRowAction = (id: string, kind?: "dismiss" | "restore"): boolean => {
    const target = listAreaRef.current?.querySelector<HTMLElement>(
      `[data-notification-action="${CSS.escape(id)}"]` +
        (kind ? `[data-action-kind="${kind}"]` : ""),
    );
    target?.focus();
    return Boolean(target);
  };

  const hide = (item: NotificationItem, hidden: boolean) => {
    if (hidden) {
      if (showHidden) {
        // The row stays, muted: its Restore button takes the ×'s place.
        // Focused after the refetch renders it (see the effect below).
        pendingRestoreFocus.current = item.id;
      } else {
        const at = items.findIndex((other) => other.id === item.id);
        const neighbour = items[at + 1] ?? items[at - 1];
        if (!neighbour || !focusRowAction(neighbour.id)) focusListWhenEmpty.current = true;
      }
    }
    setHidden.mutate(
      { id: item.id, hidden },
      { onError: (err) => toast(apiErrorText(t, err), "error") },
    );
  };

  // Runs after every render: the target appears only once the refetch
  // has replaced the row (or emptied the list).
  React.useEffect(() => {
    if (focusListWhenEmpty.current && items.length === 0 && !list.isLoading) {
      listAreaRef.current?.focus();
      focusListWhenEmpty.current = false;
    }
    const restoreOf = pendingRestoreFocus.current;
    if (restoreOf && focusRowAction(restoreOf, "restore")) pendingRestoreFocus.current = null;
  });

  const clearAll = async () => {
    if (!newestShown) return;
    const ok = await confirm({
      title: t("notifications.clear_all_confirm_title"),
      message: t("notifications.clear_all_confirm_body"),
      confirmLabel: t("notifications.clear_all"),
    });
    if (!ok) return;
    dismissAll.mutate(newestShown, {
      onError: (err) => toast(apiErrorText(t, err), "error"),
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-labelledby": TITLE_ID,
          sx: { width: { xs: "100vw", sm: 400 }, maxWidth: "100vw" },
        },
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 1, px: 2, py: 1, minHeight: 56 }}
      >
        <Typography id={TITLE_ID} variant="h6" component="h2" sx={{ flex: 1, minWidth: 0 }}>
          {t("notifications.title")}
        </Typography>
        <IconButton edge="end" onClick={onClose} aria-label={t("common.close")}>
          <Close />
        </IconButton>
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: "center", flexWrap: "wrap", columnGap: 1, px: 1.5, pb: 1 }}
      >
        <Button
          size="small"
          disabled={unread === 0 || markAll.isPending}
          onClick={() => markAll.mutate(undefined)}
        >
          {t("notifications.mark_all_read")}
        </Button>
        <Button
          size="small"
          disabled={newestShown === null || dismissAll.isPending}
          onClick={() => void clearAll()}
        >
          {t("notifications.clear_all")}
        </Button>
        <FormControlLabel
          sx={{ ml: "auto", mr: 0 }}
          control={
            <Switch
              size="small"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
            />
          }
          label={<Typography variant="body2">{t("notifications.show_hidden")}</Typography>}
        />
      </Stack>
      <Divider />
      {list.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress size={24} aria-label={t("common.loading")} />
        </Box>
      ) : items.length === 0 ? (
        <Stack
          ref={(el: HTMLDivElement | null) => {
            listAreaRef.current = el;
          }}
          tabIndex={-1}
          sx={{ alignItems: "center", gap: 1, p: 4, color: "text.secondary", outline: "none" }}
        >
          <NotificationsOutlined />
          <Typography variant="body2">
            {showHidden ? t("notifications.empty_hidden") : t("notifications.empty")}
          </Typography>
        </Stack>
      ) : (
        <List
          ref={(el: HTMLUListElement | null) => {
            listAreaRef.current = el;
          }}
          tabIndex={-1}
          disablePadding
          sx={{ overflowY: "auto", outline: "none" }}
        >
          {items.map((item) => (
            <NotificationRow
              key={item.id}
              item={item}
              otherOrg={otherOrg(item)}
              onOpen={() => void openItem(item)}
              onHide={(hidden) => hide(item, hidden)}
            />
          ))}
          {list.hasNextPage && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
              <Button
                size="small"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                {t("notifications.load_more")}
              </Button>
            </Box>
          )}
        </List>
      )}
    </Drawer>
  );
}

function NotificationRow(props: {
  item: NotificationItem;
  otherOrg: string | null;
  onOpen: () => void;
  onHide: (hidden: boolean) => void;
}): React.JSX.Element {
  const { item, otherOrg, onOpen, onHide } = props;
  const { t } = useTranslation();
  // The drawer can stay open a while with no new notification arriving
  // to re-render this row; tick the age along on our own.
  const now = useNow();
  const Icon = (item.stem && ICONS[item.stem]) || NotificationsOutlined;
  const hidden = item.dismissed_at !== null;
  // A hidden row is muted even when it was never read: no bold, no dot,
  // no "Unread" for a screen reader. Restoring brings it back unread.
  const unread = !item.read_at && !hidden;
  const title = notificationTitle(t, item);
  return (
    <ListItem
      disablePadding
      // Room for the row's action (× or Restore) on the right; ListItem's
      // own 48px is too narrow for the Restore button.
      sx={{ "& > .MuiListItemButton-root": { pr: hidden ? 13 : 6 } }}
      secondaryAction={
        hidden ? (
          <Button
            size="small"
            data-notification-action={item.id}
            data-action-kind="restore"
            onClick={() => onHide(false)}
            aria-label={t("notifications.restore_aria", { title })}
          >
            {t("notifications.restore")}
          </Button>
        ) : (
          <Tooltip title={t("notifications.dismiss")}>
            <IconButton
              edge="end"
              size="small"
              data-notification-action={item.id}
              data-action-kind="dismiss"
              onClick={() => onHide(true)}
              aria-label={t("notifications.dismiss_aria", { title })}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    >
      <ListItemButton
        onClick={onOpen}
        alignItems="flex-start"
        sx={{
          minHeight: 56,
          gap: 0.5,
          bgcolor: unread ? "action.hover" : undefined,
          opacity: hidden ? 0.6 : undefined,
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, mt: 0.5, color: unread ? "primary.main" : undefined }}>
          <Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={
            <>
              {hidden && (
                <>
                  <Box component="span" sx={SCREEN_READER_ONLY}>
                    {t("notifications.hidden_sr")}
                  </Box>{" "}
                </>
              )}
              {unread && (
                <>
                  <Box component="span" sx={SCREEN_READER_ONLY}>
                    {t("notifications.unread_sr")}
                  </Box>{" "}
                </>
              )}
              {title}
            </>
          }
          secondary={
            <Stack
              component="span"
              direction="row"
              sx={{ alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.25 }}
            >
              <span>{relativeAge(item.created_at, now)}</span>
              {otherOrg && (
                <Chip
                  component="span"
                  size="small"
                  variant="outlined"
                  label={otherOrg}
                  sx={{ maxWidth: 180 }}
                />
              )}
            </Stack>
          }
          slotProps={{
            primary: {
              sx: {
                fontWeight: unread ? 600 : 400,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                overflowWrap: "anywhere",
              },
            },
            secondary: { component: "span" },
          }}
        />
        {unread && (
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "primary.main",
              flexShrink: 0,
              mt: 1.25,
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
}
